import {getSequenceParts} from "@/lib/activeCourseOffering";
import type {
	FullCourse,
	FullCourseOffering,
} from "@repo/sanity-utils/query-types";

export const COURSE_SECTION_IDS = {
	info: "info",
	formats: "formats",
	sequence: "sequence",
	cta: "cta",
} as const;

export type CourseSectionId =
	(typeof COURSE_SECTION_IDS)[keyof typeof COURSE_SECTION_IDS];

function getCourseSectionScrollTop(section: HTMLElement): number {
	const header = section.firstElementChild;
	const anchor =
		header?.firstElementChild ?? section.querySelector("h2") ?? section;
	return anchor.getBoundingClientRect().top + window.scrollY;
}

/**
 * Smooth-scroll to a course-page section after the next paint so newly
 * mounted sections (e.g. Sequence after format select) are in the DOM.
 * Targets the step tag when present so "STEP N" lands at the viewport top.
 */
export function scrollToCourseSection(id: CourseSectionId | string) {
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			const section = document.getElementById(id);
			if (!section) {
				return;
			}

			const offsetTop = getCourseSectionScrollTop(section);
			window.scrollTo({top: offsetTop, behavior: "smooth"});
		});
	});
}

/**
 * After the user picks a learning format, scroll to Sequence when that
 * section will render; otherwise to the CTA card.
 */
export function getFormatAutoScrollTarget({
	sequenceType,
	courseOfferings,
	selectedModalityId,
}: {
	sequenceType: NonNullable<FullCourse>["sequenceType"];
	courseOfferings: FullCourseOffering[];
	selectedModalityId: string;
}): typeof COURSE_SECTION_IDS.sequence | typeof COURSE_SECTION_IDS.cta {
	if (sequenceType === "versions") {
		return COURSE_SECTION_IDS.cta;
	}

	const parts = getSequenceParts({
		courseOfferings,
		selectedModalityId,
	});

	if (parts.length >= 2) {
		return COURSE_SECTION_IDS.sequence;
	}

	return COURSE_SECTION_IDS.cta;
}

/**
 * Whether a user selection should trigger the one-time auto-scroll.
 * Re-clicking the current selection never scrolls (and never deselects).
 */
export function shouldAutoScrollOnSelection({
	allowAutoScroll,
	alreadyScrolled,
	previousSelectedId,
	nextSelectedId,
}: {
	allowAutoScroll: boolean;
	alreadyScrolled: boolean;
	previousSelectedId: string | null | undefined;
	nextSelectedId: string;
}): boolean {
	if (previousSelectedId === nextSelectedId) {
		return false;
	}
	return allowAutoScroll && !alreadyScrolled;
}
